import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-administration';

export type PersonRow = components['schemas']['PersonRow'];
export type InvitationRow = components['schemas']['InvitationRow'];
export type PeopleItem = PersonRow | InvitationRow;
export type PeopleStatus = components['schemas']['PeopleStatus'];

export type PeopleFilters = {
  readonly q: string;
  readonly roleId: string | null;
  readonly status: PeopleStatus | null;
  readonly sort: 'name' | 'updated';
};

export type PeopleState =
  | { readonly status: 'loading'; readonly items: readonly PeopleItem[] }
  | {
      readonly status: 'ready';
      readonly items: readonly PeopleItem[];
      readonly total: number;
      readonly hasMore: boolean;
      /** «Mostra altri» is reading the next page; the rows already shown stay. */
      readonly loadingMore: boolean;
    }
  | {
      readonly status: 'error';
      readonly items: readonly PeopleItem[];
      readonly code: unknown;
      readonly httpStatus: number | undefined;
    };

export function itemKey(item: PeopleItem): string {
  return item.kind === 'user' ? `user:${item.id}` : `invitation:${item.invitation.id}`;
}

type Page = { readonly total: number; readonly nextCursor: string | null };

function readyState<T extends PeopleItem>(page: Page, items: readonly T[]): PeopleState {
  return {
    status: 'ready',
    items,
    total: page.total,
    hasMore: page.nextCursor !== null,
    loadingMore: false,
  };
}

/** The rows the undo window hides at once, and brings back on «Annulla» or a failed write. */
function useHiddenRows() {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const hide = useCallback((key: string) => setHidden((current) => new Set(current).add(key)), []);
  const restore = useCallback(
    (key: string) =>
      setHidden((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      }),
    [],
  );
  return { hidden, hide, restore };
}

function usePeopleRead(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
  filters: PeopleFilters,
) {
  const { q, roleId, status, sort } = filters;
  const read = useCallback(
    (pageCursor: string | null) =>
      apiClient.GET('/v1/administration/{placeKind}/{placeId}/people', {
        params: {
          path: { placeKind, placeId },
          query: {
            ...(q !== '' ? { q } : {}),
            ...(roleId !== null ? { roleId } : {}),
            ...(status !== null ? { status } : {}),
            sort,
            ...(pageCursor !== null ? { cursor: pageCursor } : {}),
          },
        },
      }),
    [placeKind, placeId, q, roleId, status, sort],
  );
  return read;
}

/**
 * Utenti's rows: the people of a place and its pending invitations, as one server-side page —
 * searched, filtered and sorted by the server (the screen standard: lists are paginated, filtered
 * and sorted server-side), with «Mostra altri» through the cursor.
 *
 * `hide` and `restore` are the undo window's optimistic part: a row leaves at once when «Rimuovi
 * da qui» is pressed, and comes back on «Annulla» or when the withheld write fails.
 */
export function usePeople(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
  filters: PeopleFilters,
): {
  readonly state: PeopleState;
  readonly more: () => void;
  readonly reload: () => void;
  readonly hide: (key: string) => void;
  readonly restore: (key: string) => void;
  readonly hidden: ReadonlySet<string>;
} {
  const [state, setState] = useState<PeopleState>({ status: 'loading', items: [] });
  const [cursor, setCursor] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { hidden, hide, restore } = useHiddenRows();
  // Every first page starts a new generation: a «Mostra altri» page asked for an earlier one is
  // dropped when it answers, instead of being appended to a list it no longer belongs to.
  const generation = useRef(0);
  const read = usePeopleRead(placeKind, placeId, filters);

  useEffect(() => {
    generation.current += 1;
    let cancelled = false;
    setState({ status: 'loading', items: [] });
    setCursor(null);
    void read(null).then(
      ({ data, error, response }) => {
        if (cancelled) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({ status: 'error', items: [], code: error, httpStatus: response.status });
          return;
        }
        setCursor(data.nextCursor);
        setState(readyState(data, data.items));
      },
      () => {
        if (!cancelled) {
          setState({ status: 'error', items: [], code: null, httpStatus: undefined });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [read, attempt]);

  const more = useCallback(() => {
    if (state.status !== 'ready' || cursor === null || state.loadingMore) {
      return;
    }
    const before = state;
    const asked = generation.current;
    setState({ ...before, loadingMore: true });
    void read(cursor).then(
      ({ data, error, response }) => {
        if (generation.current !== asked) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({
            status: 'error',
            items: before.items,
            code: error,
            httpStatus: response.status,
          });
          return;
        }
        setCursor(data.nextCursor);
        setState(readyState(data, [...before.items, ...data.items]));
      },
      () => {
        if (generation.current === asked) {
          setState({ status: 'error', items: before.items, code: null, httpStatus: undefined });
        }
      },
    );
  }, [state, cursor, read]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { state, more, reload, hide, restore, hidden };
}
